import { useCallback } from 'react'

import { noop } from 'lodash-es'

import { ProviderProgramTaxonomy } from '../../../models/providers/Providers'
import { YearProgramCoverage } from '../../../models/user/User'
import { DirectLinkIntent } from '../../common/constants/customerProgram'
import { TREATMENT_OPTIONS_TYPE } from '../../common/constants/treatmentOptions'
import { isValidTreatmentOptionForDirectPath } from '../../common/utils/commonUtils'
import { getProgramFromTreatmentOption, shouldNavigateToHPIPage } from '../../healthPlan/utils'
import { getTriageTreatmentOptionFromCTPO, isValidCTPOCombination } from '../../programConfigs'
import { DirectLinkSource } from '../constants'

interface useNavigateToDirectPathBookingArgs {
  setSelectedTreatmentOptionCTPO: (ctpo: ProviderProgramTaxonomy) => void
  setSelectedTreatmentOption: (treatmentOption?: TREATMENT_OPTIONS_TYPE) => void
  setDirectLinkUtmSource: (utm_source: string) => void
  setIsCurrentSearchDirectPath: (isDirectPath: boolean) => void
  setDirectLinkIntent: (directLinkIntent: string) => void
  setDirectLinkSource: (directLinkSource: DirectLinkSource) => void
  clearDirectPathState: () => void
  navigate: {
    goToHPIIneligible: () => void
    goToHPIPage: () => void
    goToLocationFinder: () => void
    goToOnsiteLocationFinder: () => void
    goToInternationalConsentForDataCollection: () => void
    goToTreatmentRecommendations: () => void
  }
}

/**
 * Hook that handles setting state variable needed for direct path navigation
 */
export const useNavigateToDirectPathBooking = ({
  setSelectedTreatmentOptionCTPO,
  setSelectedTreatmentOption,
  setIsCurrentSearchDirectPath,
  setDirectLinkIntent,
  setDirectLinkUtmSource,
  setDirectLinkSource,
  clearDirectPathState,
  navigate: {
    goToHPIPage = noop,
    goToHPIIneligible = noop,
    goToLocationFinder = noop,
    goToOnsiteLocationFinder = noop,
    goToInternationalConsentForDataCollection = noop,
    goToTreatmentRecommendations = noop,
  },
}: useNavigateToDirectPathBookingArgs) => {
  return useCallback(
    ({
      ctpo,
      treatmentOption,
      directLinkUtmSource,
      directLinkIntent,
      directLinkSource,
      isHealthPlanActive,
      getIsEAPSessionCountExhausted,
      currentProgramCoverageBreakdown,
      shouldSeeInternationalConsentForDataCollection,
      isInternationalUserWithDeclinedInternationalConsent,
    }: {
      ctpo: Partial<ProviderProgramTaxonomy>
      treatmentOption?: string
      directLinkUtmSource?: string
      directLinkIntent?: string
      directLinkSource?: DirectLinkSource
      isHealthPlanActive: boolean
      getIsEAPSessionCountExhausted: (options: { treatmentOption?: TREATMENT_OPTIONS_TYPE | undefined }) => boolean
      currentProgramCoverageBreakdown: YearProgramCoverage
      shouldSeeInternationalConsentForDataCollection: boolean
      isInternationalUserWithDeclinedInternationalConsent: boolean
    }) => {
      clearDirectPathState()
      setIsCurrentSearchDirectPath(true)
      const finalTreamentOption = isValidCTPOCombination(ctpo)
        ? getTriageTreatmentOptionFromCTPO(ctpo)
        : (treatmentOption as TREATMENT_OPTIONS_TYPE)
      if (isValidCTPOCombination(ctpo)) {
        setSelectedTreatmentOptionCTPO(ctpo)
      }
      if (isValidTreatmentOptionForDirectPath(finalTreamentOption)) {
        setSelectedTreatmentOption(finalTreamentOption)
      }
      if (directLinkIntent) {
        setDirectLinkIntent(directLinkIntent)
      }
      if (directLinkSource) {
        setDirectLinkSource(directLinkSource)
      }
      if (directLinkUtmSource) {
        setDirectLinkUtmSource(directLinkUtmSource)
      }

      const isEAPSessionCountExhausted = getIsEAPSessionCountExhausted({ treatmentOption: finalTreamentOption })
      if (shouldSeeInternationalConsentForDataCollection) {
        return goToInternationalConsentForDataCollection()
      } else if (isInternationalUserWithDeclinedInternationalConsent) {
        return goToTreatmentRecommendations() // todo: match behavior when going through s4c triage. start a shared hook?
      } else if (
        !!finalTreamentOption &&
        !getProgramFromTreatmentOption({
          treatmentOptionName: finalTreamentOption ?? null,
          customerPrograms: currentProgramCoverageBreakdown,
          allowPartialTreatmentOptions: true,
        })
      ) {
        return goToHPIIneligible()
      } else if (
        shouldNavigateToHPIPage({
          treatmentOption: finalTreamentOption,
          customerPrograms: currentProgramCoverageBreakdown,
          isHealthPlanActive,
          isEAPSessionCountExhausted,
          allowPartialTreatmentOptions: true,
        })
      ) {
        return goToHPIPage()
      }

      if (directLinkIntent === DirectLinkIntent.ONSITE_THERAPY) {
        return goToOnsiteLocationFinder()
      }
      return goToLocationFinder()
    },
    [
      clearDirectPathState,
      goToHPIIneligible,
      goToHPIPage,
      goToInternationalConsentForDataCollection,
      goToLocationFinder,
      goToOnsiteLocationFinder,
      goToTreatmentRecommendations,
      setDirectLinkIntent,
      setDirectLinkSource,
      setIsCurrentSearchDirectPath,
      setSelectedTreatmentOption,
      setSelectedTreatmentOptionCTPO,
      setDirectLinkUtmSource,
    ],
  )
}
